import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import "@/index.css";
import "@/lib/pointer";
import LoadingScreen from "@/components/LoadingScreen";
import Navbar from "@/components/Navbar";

const PRELOAD_IMAGES = [
    "/images/hero-villa.jpg",
    "/images/kerala-courtyard.jpg",
    "/images/logo.jpg",
    "/images/glyph.png",
    "/images/ceo.jpg",
    "/images/projects/monsoon-villa.jpg",
    "/images/projects/rthanya-studio.jpg",
    "/images/projects/kolam-house.jpg",
    "/images/projects/bricklane-cafe.jpg",
    "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc4NTUwMDE1NXww&ixlib=rb-4.1.0&q=85",
];
import Cursor from "@/components/Cursor";
import ScrollProgress from "@/components/ScrollProgress";
import WhatsAppButton from "@/components/WhatsAppButton";
import LightboxProvider from "@/components/Lightbox";
import Footer from "@/sections/Footer";
import Home from "@/pages/Home";
import StudioPage from "@/pages/StudioPage";
import ServicesPage from "@/pages/ServicesPage";
import PortfolioPage from "@/pages/PortfolioPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import ContactPage from "@/pages/ContactPage";

function ScrollManager() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.__lenis?.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function AnimatedRoutes({ introDone }) {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route path="/" element={<Home introDone={introDone} />} />
                <Route path="/studio" element={<StudioPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/portfolio/:slug" element={<ProjectDetailPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </AnimatePresence>
    );
}

function App() {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        PRELOAD_IMAGES.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    useEffect(() => {
        const lenis = window.__lenis;
        if (!lenis) return;
        if (loading) lenis.stop();
        else lenis.start();
    }, [loading]);

    // Site-wide image protection — block right-click, drag, and long-press save on all images
    useEffect(() => {
        const isImage = (el) => el?.tagName === "IMG";
        const onContext = (e) => {
            if (isImage(e.target)) e.preventDefault();
        };
        const onDrag = (e) => {
            if (isImage(e.target)) e.preventDefault();
        };
        document.addEventListener("contextmenu", onContext);
        document.addEventListener("dragstart", onDrag);
        return () => {
            document.removeEventListener("contextmenu", onContext);
            document.removeEventListener("dragstart", onDrag);
        };
    }, []);

    return (
        <BrowserRouter>
            <LightboxProvider>
                <ScrollManager />
                <div className="relative min-h-screen bg-coal font-body text-white">
                    <Cursor />
                    <ScrollProgress />
                    <AnimatePresence>
                        {loading && (
                            <LoadingScreen onComplete={() => setLoading(false)} />
                        )}
                    </AnimatePresence>
                    <Navbar />
                    <AnimatedRoutes introDone={!loading} />
                    <Footer />
                    <WhatsAppButton />
                    <Toaster
                        theme="dark"
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "#141414",
                                border: "1px solid rgba(255,255,255,0.08)",
                                color: "#fff",
                            },
                        }}
                    />
                </div>
            </LightboxProvider>
        </BrowserRouter>
    );
}

export default App;
